import FadeUpText from "@/components/ui/FadeUp";
import SleekButton from "@/components/ui/NavButtons";
import TextType from "@/components/ui/Type";
import type { FC } from "react";
import { Link } from "react-router-dom";

const HERO_BG =
  "https://api.builder.io/api/v1/image/assets/2786a9b4d60a41fc839971aa8ed25ba5/40d2e7a4f6ae27917bedcdbd8e595db0adb08688?placeholderIfAbsent=true";

interface HeroSectionProps {
  onScrollToReturnModel: () => void;
  onScrollToSolution: () => void;
  onScrollDown?: () => void;
}

export const HeroSection: FC<HeroSectionProps> = ({
  onScrollToReturnModel: _onScrollToReturnModel,
  onScrollToSolution: _onScrollToSolution,
  onScrollDown,
}) => {
  return (
    <section className="relative flex min-h-[calc(100dvh-3.5rem-env(safe-area-inset-top,0px))] w-full flex-col overflow-x-hidden bg-[#f0f4ff] sm:min-h-[calc(100dvh-4rem-env(safe-area-inset-top,0px))] md:min-h-[calc(100dvh-72px-env(safe-area-inset-top,0px))] lg:min-h-[calc(100dvh-5rem-env(safe-area-inset-top,0px))] xl:min-h-[calc(100dvh-84px-env(safe-area-inset-top,0px))] dark:bg-[#020D30]">
      {/* Height comes from this block; background layers are absolute */}
      <div className="relative z-20 flex flex-1 flex-col">
        <div className="flex flex-1 flex-col justify-center px-6 pb-6 pt-10 sm:px-10 sm:pb-8 sm:pt-12 md:px-12 md:max-lg:justify-start md:max-xl:pb-5 md:max-xl:pt-9 lg:pb-10 lg:pt-14 lg:max-xl:pb-6 lg:max-xl:pt-10 lg:pl-24 lg:pr-12 lg:max-xl:pl-20 lg:max-xl:pr-10 xl:max-[1366px]:pb-6 xl:max-[1366px]:pt-10 xl:max-[1366px]:pl-20 xl:max-[1366px]:pr-10 min-[1367px]:pb-10 min-[1367px]:pt-16">
        <div className="max-w-xl translate-y-3 sm:translate-y-2 md:translate-y-2 md:max-lg:translate-y-4 md:max-lg:max-w-2xl lg:translate-y-1 lg:max-xl:translate-y-3 xl:max-[1366px]:translate-y-3 min-[1367px]:translate-y-0">
          <FadeUpText>
            <div className="mb-3 inline-flex items-center rounded-full border border-blue-200 bg-blue-100 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-700 shadow-sm shadow-blue-900/[0.04] dark:border-blue-500/20 dark:bg-blue-600/15 dark:text-blue-400 dark:shadow-none sm:mb-4 sm:px-3.5 sm:py-2 sm:text-sm">
              Investment Through Intelligence
            </div>
          </FadeUpText>

          <div className="mx-auto max-w-3xl">
            <h1 className="mx-auto mt-2 min-h-[9.5rem] font-alexandria text-4xl leading-snug font-extrabold text-gray-900 dark:text-white sm:mt-3 sm:min-h-[6.5rem] sm:text-5xl">
              <TextType
                as="span"
                text={[
                  "Stop Guessing Your\nNext Investment.",
                  "Know When You're\nReady to Buy.",
                  "Your Portfolio.\nActually Understood.",
                ]}
                typingSpeed={75}
                pauseDuration={1500}
                showCursor
                cursorCharacter="|"
                className="inline-block w-full whitespace-pre-line"
              />
            </h1>
          </div>

          <FadeUpText className="mb-5 sm:mb-6">
            <p className="max-w-lg text-sm leading-relaxed sm:text-base dark:[text-shadow:0_1px_3px_rgba(0,0,0,0.55)]">
              <span className="text-gray-700 dark:text-slate-200">
                Know if you&apos;re financially ready — and how your portfolio is actually performing —{" "}
              </span>
              <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text font-semibold text-transparent dark:from-blue-400 dark:to-cyan-400">
                before you invest another dollar.
              </span>
              <span className="text-gray-700 dark:text-slate-200"> No spreadsheets. No finance degree.</span>
            </p>
          </FadeUpText>

          <FadeUpText>
            <div className="mb-3 flex flex-wrap gap-4 max-sm:flex-col">
              <a
                href="https://app.vawena.ai/signup"
                target="_self"
                rel="noopener noreferrer"
                className="max-sm:w-full"
              >
                <SleekButton variant="primary" className="max-sm:w-full max-sm:justify-center">
                  Start Free 14-Day Trial
                </SleekButton>
              </a>
              <Link to="/quiz" className="max-sm:w-full">
                <SleekButton variant="light" className="max-sm:w-full max-sm:justify-center">
                  Take the Readiness Quiz — Free
                </SleekButton>
              </Link>
            </div>
          </FadeUpText>

          <FadeUpText>
            <p className="mb-4 text-xs text-gray-500 dark:text-slate-400 sm:mb-5">No spreadsheets. No guesswork. Instant clarity.</p>
          </FadeUpText>

          <FadeUpText>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-600 dark:text-slate-400/90">
              {["14-day free trial", "Cancel anytime", "We'll remind you before you're charged"].map(t => (
                <span key={t} className="flex items-center gap-1.5">
                  <svg
                    className="h-3 w-3 shrink-0 text-blue-600 dark:text-blue-400"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                  </svg>
                  {t}
                </span>
              ))}
            </div>
          </FadeUpText>
        </div>
        </div>
      </div>

      {/* ── Background stack (pointer-events-none so CTAs work) ── */}
      <div
        className="pointer-events-none absolute inset-0 z-0 hidden bg-gradient-to-r from-[#000F38] via-[#020D30] to-[#020D30] dark:block"
        aria-hidden
      />

      <div
        className="pointer-events-none absolute inset-0 z-[1] grid h-full min-h-0 grid-cols-1 grid-rows-[minmax(140px,min(32dvh,220px))_minmax(0,1fr)] md:grid-rows-[minmax(160px,min(34dvh,240px))_minmax(0,1fr)] md:max-xl:grid-rows-[minmax(120px,min(28dvh,200px))_minmax(0,1fr)] lg:grid-cols-2 lg:grid-rows-1"
        aria-hidden
      >
        <div className="relative min-h-0 overflow-hidden bg-[#f0f4ff] lg:min-h-full dark:bg-transparent">
          <img
            src={HERO_BG}
            alt=""
            className="hidden absolute inset-0 h-full w-full object-cover dark:block
              [mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.38),rgba(0,0,0,0.06))]
              [mask-repeat:no-repeat] [mask-size:100%_100%]
              lg:[mask-image:linear-gradient(to_right,rgba(0,0,0,0.42),rgba(0,0,0,0))]
              dark:[mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.82),rgba(0,0,0,0.2))]
              dark:[mask-repeat:no-repeat] dark:[mask-size:100%_100%]
              lg:dark:[mask-image:linear-gradient(to_right,rgba(0,0,0,0.88),rgba(0,0,0,0))]"
          />
        </div>
        <div className="relative flex min-h-0 items-center justify-center bg-[#f0f4ff] px-3 pb-6 pt-4 dark:bg-transparent md:px-8 md:py-8 md:max-xl:px-5 md:max-xl:py-3 md:max-xl:pb-4 lg:min-h-full lg:px-6 lg:py-10 lg:max-xl:py-3 lg:max-xl:px-5 lg:max-xl:pb-4 xl:max-[1366px]:py-3 xl:max-[1366px]:px-5 xl:max-[1366px]:pb-4 min-[1367px]:py-10 min-[1367px]:px-6">
          <img
            src="/assets/hero-section/building.gif"
            alt=""
            className="w-full max-h-[min(42dvh,420px)] object-contain object-center sm:max-h-[min(44dvh,480px)] md:max-h-[min(46dvh,520px)] md:max-xl:max-h-[min(38dvh,400px)] lg:max-h-[min(78dvh,800px)] lg:max-xl:max-h-[min(70dvh,640px)] xl:max-[1366px]:max-h-[min(70dvh,640px)] min-[1367px]:max-h-[min(82dvh,840px)]"
          />
        </div>
      </div>

      {onScrollDown && (
        <div className="relative z-30 flex shrink-0 justify-center pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pt-1">
          <button
            type="button"
            onClick={onScrollDown}
            className="flex flex-col items-center gap-0 rounded-full px-3 py-1 text-blue-600 opacity-90 transition hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 dark:text-blue-400 motion-safe:animate-bounce"
            aria-label="Scroll down to continue"
          >
            {[0, 1, 2].map(i => (
              <svg
                key={i}
                className={`h-5 w-5 shrink-0 drop-shadow-sm sm:h-6 sm:w-6 ${i === 0 ? "opacity-40" : i === 1 ? "opacity-70" : "opacity-100"} ${i > 0 ? "-mt-2.5" : ""}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            ))}
          </button>
        </div>
      )}
    </section>
  );
};
